// src/pages/LandingPage.tsx
import React from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Stack,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { changeLanguage as changeAppLanguage } from '../i18n';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChurchIcon from '@mui/icons-material/Church';
import SecurityIcon from '@mui/icons-material/Security';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import MenuIcon from '@mui/icons-material/Menu';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import LanguageIcon from '@mui/icons-material/Language';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const APP_STORE_URL = 'https://apps.apple.com/app/zemen-calendar/id6759289221';
  const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.ethiopiancalendar.app';

  const handleDownloadClick = (platform: 'ios' | 'android') => {
    const url = platform === 'ios' ? APP_STORE_URL : PLAY_STORE_URL;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleLanguageClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setAnchorEl(null);
  };

  const changeLanguage = (lng: string) => {
    changeAppLanguage(lng); // Persiste dans localStorage
    handleLanguageClose();
  };

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'ti', name: 'ትግርኛ', flag: '🇪🇷' },
    { code: 'am', name: 'አማርኛ', flag: '🇪🇹' },
  ];

  const features = [
    {
      icon: <DashboardIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
      title: t('features.dashboard.title'),
      description: t('features.dashboard.description'),
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
      title: t('features.users.title'),
      description: t('features.users.description'),
    },
    {
      icon: <CalendarMonthIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
      title: t('features.calendar.title'),
      description: t('features.calendar.description'),
    },
    {
      icon: <ChurchIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
      title: t('features.multi.title'),
      description: t('features.multi.description'),
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
      title: t('features.security.title'),
      description: t('features.security.description'),
    },
    {
      icon: <CloudSyncIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
      title: t('features.sync.title'),
      description: t('features.sync.description'),
    },
  ];

  return (
    <Box sx={{ bgcolor: 'background.default' }}>
      {/* Header / Navigation */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar>
          <Box
                component="img"
                src="/logo512.png"
                alt="Zemen Logo"
                sx={{ mr: 2, height: 36, width: 36 }}
              />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'text.primary', fontWeight: 700 }}>
            {t('nav.brand')}
          </Typography>
          {!isMobile && (
            <Stack direction="row" spacing={3} sx={{ mr: 3 }}>
              <Button color="inherit" sx={{ color: 'text.primary' }} href="#features">
                {t('nav.features')}
              </Button>
              <Button color="inherit" sx={{ color: 'text.primary' }} href="#app-preview">
                {t('nav.preview')}
              </Button>
              <Button color="inherit" sx={{ color: 'text.primary' }} href="#download">
                {t('nav.download')}
              </Button>
              <Button color="inherit" sx={{ color: 'text.primary' }} href="#contact">
                {t('nav.contact')}
              </Button>
            </Stack>
          )}
          {/* Language Selector */}
          <IconButton
            color="primary"
            onClick={handleLanguageClick}
            sx={{ mr: 2 }}
          >
            <LanguageIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleLanguageClose}
          >
            {languages.map((lang) => (
              <MenuItem
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                selected={i18n.language === lang.code}
              >
                <span style={{ marginRight: 8 }}>{lang.flag}</span>
                {lang.name}
              </MenuItem>
            ))}
          </Menu>
          {isMobile ? (
            <IconButton color="primary">
              <MenuIcon />
            </IconButton>
          ) : (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => navigate('/login')}
              sx={{ px: 4, borderRadius: 2 }}
            >
              {t('nav.login')}
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          backgroundImage: 'url(/zemen_hero_background.png)',
          backgroundSize: { xs: 'cover', md: '65% auto' },
          backgroundPosition: { xs: 'center', md: 'right center' },
          backgroundRepeat: 'no-repeat',
          background: {
            xs: 'url(/zemen_hero_background.png) center/cover no-repeat',
            md: `linear-gradient(135deg, #e8e0f0 0%, #f0e8f5 30%, #f5f0fa 60%, transparent 60%), url(/zemen_hero_background.png) right center/auto 100% no-repeat`,
          },
          backgroundColor: '#f5f0fa',
          minHeight: { xs: '60vh', md: '85vh' },
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 800,
                  mb: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {t('hero.title')}
              </Typography>
              <Typography variant="h5" color="text.secondary" paragraph sx={{ mb: 4, lineHeight: 1.7 }}>
                {t('hero.description')}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{ px: 5, py: 1.5, borderRadius: 2, fontSize: '1.1rem' }}
                >
                  {t('hero.cta_start')}
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  href="#features"
                  sx={{ px: 5, py: 1.5, borderRadius: 2, fontSize: '1.1rem' }}
                >
                  {t('hero.cta_learn')}
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              {/* Phone mockup visible in background */}
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box
        id="features"
        sx={{
          py: { xs: 8, md: 14 },
          background: 'linear-gradient(180deg, #ffffff 0%, #f8f5fc 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: 2,
                mb: 1,
                display: 'block'
              }}
            >
              {t('features.subtitle')}
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '3rem' },
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t('features.title')}
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.7)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.8)',
                    borderRadius: 4,
                    transition: 'all 0.4s ease',
                    '&:hover': {
                      transform: 'translateY(-12px)',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                      background: 'rgba(255, 255, 255, 0.95)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Box
                      sx={{
                        mb: 3,
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* App Preview Section */}
      <Box
        id="app-preview"
        sx={{
          py: { xs: 8, md: 14 },
          background: 'linear-gradient(180deg, #f8f5fc 0%, #e8e0f0 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: 2,
                mb: 1,
                display: 'block'
              }}
            >
              {t('preview.subtitle')}
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '3rem' },
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t('preview.title')}
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {[
              { title: t('preview.dashboard_title'), desc: t('preview.dashboard_desc'), icon: <DashboardIcon sx={{ fontSize: 40, color: 'white' }} /> },
              { title: t('preview.calendar_title'), desc: t('preview.calendar_desc'), icon: <CalendarMonthIcon sx={{ fontSize: 40, color: 'white' }} /> },
              { title: t('preview.management_title'), desc: t('preview.management_desc'), icon: <PeopleIcon sx={{ fontSize: 40, color: 'white' }} /> },
            ].map((item, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    minHeight: 320,
                    borderRadius: 4,
                    background: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 4,
                    transition: 'all 0.4s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 24px 48px rgba(0, 0, 0, 0.12)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      boxShadow: `0 8px 24px ${theme.palette.primary.main}40`,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, textAlign: 'center' }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', lineHeight: 1.7 }}>
                    {item.desc}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Download Section */}
      <Box
        id="download"
        sx={{
          py: { xs: 8, md: 14 },
          background: 'linear-gradient(180deg, #e8e0f0 0%, #f8f5fc 100%)',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: 2,
                mb: 1,
                display: 'block'
              }}
            >
              {t('download.subtitle')}
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '3rem' },
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
              }}
            >
              {t('download.title')}
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center" alignItems="center">
            <Button
              variant="contained"
              size="large"
              onClick={() => handleDownloadClick('ios')}
              sx={{
                bgcolor: 'rgba(0,0,0,0.9)',
                color: '#fff',
                px: 4,
                py: 2,
                borderRadius: 3,
                minWidth: 220,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: '#000',
                  transform: 'scale(1.05)',
                },
              }}
            >
              <AppleIcon sx={{ fontSize: 36 }} />
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="caption" display="block" sx={{ opacity: 0.8, lineHeight: 1.2 }}>
                  {t('download.app_store.label')}
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {t('download.app_store.name')}
                </Typography>
              </Box>
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={() => handleDownloadClick('android')}
              sx={{
                bgcolor: '#3DDC84',
                color: '#fff',
                px: 4,
                py: 2,
                borderRadius: 3,
                minWidth: 220,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: '#2DB06B',
                  transform: 'scale(1.05)',
                },
              }}
            >
              <AndroidIcon sx={{ fontSize: 36 }} />
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="caption" display="block" sx={{ opacity: 0.9, lineHeight: 1.2 }}>
                  {t('download.google_play.label')}
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {t('download.google_play.name')}
                </Typography>
              </Box>
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box
        id="contact"
        sx={{
          py: { xs: 8, md: 14 },
          background: 'linear-gradient(180deg, #ffffff 0%, #f8f5fc 100%)',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: 2,
                mb: 1,
                display: 'block'
              }}
            >
              {t('contact.subtitle')}
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '3rem' },
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t('contact.title')}
            </Typography>
          </Box>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} sm={6}>
              <Card
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  background: 'white',
                  borderRadius: 4,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 70,
                    height: 70,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.secondary.main}20)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <EmailIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                </Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                  {t('contact.email')}
                </Typography>
                <Typography variant="body1" color="primary.main" sx={{ fontWeight: 500 }}>
                  contact@zemen.app
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  background: 'white',
                  borderRadius: 4,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 70,
                    height: 70,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.secondary.main}20)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <PhoneIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                </Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                  {t('contact.phone')}
                </Typography>
                <Typography variant="body1" color="primary.main" sx={{ fontWeight: 500 }}>
                  +33 1 23 45 67 89
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 8,
          px: 2,
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          color: 'white',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            <Grid item xs={12} md={4}>
              <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
                <Box
                  component="img"
                  src="/logo512.png"
                  alt="Zemen Logo"
                  sx={{ mr: 2, height: 40, width: 40, filter: 'brightness(0) invert(1)' }}
                />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {t('nav.brand')}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ mb: 3, opacity: 0.7, lineHeight: 1.8 }}>
                {t('footer.description')}
              </Typography>
              <Stack direction="row" spacing={1}>
                <IconButton
                  sx={{
                    color: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': { bgcolor: theme.palette.primary.main },
                  }}
                >
                  <FacebookIcon />
                </IconButton>
                <IconButton
                  sx={{
                    color: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': { bgcolor: theme.palette.primary.main },
                  }}
                >
                  <TwitterIcon />
                </IconButton>
                <IconButton
                  sx={{
                    color: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': { bgcolor: theme.palette.primary.main },
                  }}
                >
                  <InstagramIcon />
                </IconButton>
              </Stack>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                {t('footer.product')}
              </Typography>
              <Stack spacing={2}>
                <Typography
                  component="a"
                  href="#features"
                  sx={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', '&:hover': { color: 'white' } }}
                >
                  {t('nav.features')}
                </Typography>
                <Typography
                  component="a"
                  href="#app-preview"
                  sx={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', '&:hover': { color: 'white' } }}
                >
                  {t('nav.preview')}
                </Typography>
                <Typography
                  component="a"
                  href="#download"
                  sx={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', '&:hover': { color: 'white' } }}
                >
                  {t('nav.download')}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                {t('footer.company')}
              </Typography>
              <Stack spacing={2}>
                <Typography
                  component="a"
                  href="#"
                  sx={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', '&:hover': { color: 'white' } }}
                >
                  {t('footer.about')}
                </Typography>
                <Typography
                  component="a"
                  href="#"
                  sx={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', '&:hover': { color: 'white' } }}
                >
                  {t('footer.blog')}
                </Typography>
                <Typography
                  component="a"
                  href="#contact"
                  sx={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', '&:hover': { color: 'white' } }}
                >
                  {t('nav.contact')}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                {t('footer.legal')}
              </Typography>
              <Stack spacing={2}>
                <Typography
                  component="a"
                  href="#"
                  sx={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', '&:hover': { color: 'white' } }}
                >
                  {t('footer.privacy')}
                </Typography>
                <Typography
                  component="a"
                  href="#"
                  sx={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', '&:hover': { color: 'white' } }}
                >
                  {t('footer.terms')}
                </Typography>
                <Typography
                  component="a"
                  href="#"
                  sx={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', '&:hover': { color: 'white' } }}
                >
                  {t('footer.legal_notice')}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
          <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', mt: 6, pt: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ opacity: 0.6 }}>
              © {new Date().getFullYear()} {t('nav.brand')}. {t('footer.copyright')}
            </Typography>
          </Box>
        </Container>
      </Box>

    </Box>
  );
};

export default LandingPage;
